import Icon from "@components/Icon";
import { BLOCKS } from "@contentful/rich-text-types";
import type {
	IModuleTitleRichTextFields,
	IHelpsiteArticleContentFields,
} from "@types-contentful";
import { colours, isEmptyContentfulEntry, richTextHelper } from "@helpers";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import React, { useState } from "react";
import styled from "styled-components";

const FaqQuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  border: 1px solid ${colours.MIDNIGHT_BLUE};
  border-radius: 12px;
`;

const FaqQuestionButton = styled.div`
	cursor: pointer;
	background: transparent;
	border: 0;
	padding: 24px 16px 24px;
	text-align: left;

	:hover {
		border: 0px;
		outline: 0px;
	}
		
`;
const FaqAnswerPanel = styled.div<{ $isExpanded: boolean }>`
	padding: ${({ $isExpanded }) => ($isExpanded ? "0 16px 24px 16px" : "0")};
  margin: 0px;
`;

const FaqQuestion = styled.p`
  font-weight: bold;
  @media (min-width: 860px) {
    display: block;
  }
`;

const AnswerContainer = styled(motion.div)`
  overflow: hidden;
`;

const Span = styled.span`
  display: flex;
	width: 100%;
  align-items: center;
`;

const FaqAnswer = styled.p`
  white-space: pre-wrap;
  margin: 0 0 16px 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const isHelpsiteArticleContentFields = (
	faq: IModuleTitleRichTextFields | IHelpsiteArticleContentFields,
): faq is IHelpsiteArticleContentFields => "richText" in faq;

const AccordionModuleItems = ({
	faq,
}: { faq: IModuleTitleRichTextFields | IHelpsiteArticleContentFields }) => {
	const isHelpsiteArticleContent = isHelpsiteArticleContentFields(faq);
	const name = faq.name;
	const title = isHelpsiteArticleContent ? faq.name : faq.title;
	const content = isHelpsiteArticleContent ? faq.richText : faq.content;
	const position = name ?? title; // fallback if name is undefined

	const iconSizes = {
		iconMobile: 24,
		iconTablet: 24,
		iconLaptop: 40,
		iconDesktop: 40,
		iconDesktopLarge: 40,
	};

	const pathname = usePathname();

	const variants = {
		open: { opacity: 1, height: "auto" },
		closed: { opacity: 0, height: 0 },
	};

	const [displayAnswer, setDisplayAnswer] = useState(false);

	const hideAnswer = () => setDisplayAnswer(false);

	const showAnswer = () => setDisplayAnswer(true);

	const onQuestionClick = () => {
		mixpanelTrack("accordion_interacted", {
			page_name: pathname,
			page_position: position,
			page_title: title,
		});
		if (displayAnswer) {
			hideAnswer();
		} else {
			showAnswer();
		}
	};

	const richTextOption = {
		renderNode: {
			// both props needed - render empty paragraphs as line breaks to preserve spacing
			[BLOCKS.PARAGRAPH]: (_node, children) => {
				// Check if paragraph is empty (represents additional line breaks)
				const isEmpty =
					isEmptyContentfulEntry(children) ||
					!children ||
					(Array.isArray(children) &&
						children.every(
							(child) =>
								!child || (typeof child === "string" && child.trim() === ""),
						));

				// Render empty paragraphs as a br tag to preserve multiple line breaks
				return isEmpty ? <br /> : <FaqAnswer>{children}</FaqAnswer>;
			},
		},
	};
	const renderFaqAnswer = () =>
		!isEmptyContentfulEntry(content) && (
			<AnswerContainer
				variants={variants}
				initial="closed"
				animate={displayAnswer ? "open" : "closed"}
				transition={{ duration: 0.3 }}
				aria-hidden={!displayAnswer}
			>
				{richTextHelper(content, richTextOption, undefined, !displayAnswer)}
			</AnswerContainer>
		);
	
	//creating separate ids for each accordion item
	const panelId = `accordion-panel-${String(title ?? "item")
		.replace(/\s+/g, "-")
		.replace(/[^a-zA-Z0-9-_]/g, "")}`;

	const renderFaqQuestion = () => {
		if (isEmptyContentfulEntry(title)) {
			return null;
		}

		return (
			<FaqQuestionContainer>
				<FaqQuestionButton
					onClick={onQuestionClick}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " " ) {
							e.preventDefault();
							onQuestionClick();
						}
					}}
					role = "button"
					tabIndex={0}
					data-gtm-cta-text={title}
					data-gtm-cta-position={position}
					aria-expanded={displayAnswer}
					id={panelId + "button"}
					aria-controls={panelId}
				>
					<Span>
						<FaqQuestion>{title}</FaqQuestion>
						<Icon
							iconName={displayAnswer ? "chevron-up" : "chevron-down"}
							style={{ marginLeft: "auto" }}
							iconSizes={iconSizes}
							alt=""
						/>
					</Span>
				</FaqQuestionButton>
				<FaqAnswerPanel
					id={panelId}
					$isExpanded={displayAnswer}
					aria-labelledby={panelId + "button"}
					role="region"
				>
					{renderFaqAnswer()}
				</FaqAnswerPanel>
			</FaqQuestionContainer>
		);
	};

	return (
		renderFaqQuestion()
	);
};

export default AccordionModuleItems;